import { useMutation } from "@apollo/client/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";

import { AccountItemBlock, AccountItemLine } from "./AccountItem";
import FeatureToggle from "./FeatureToggle";
import { ObjectiveNormalDefaultsForm } from "./ObjectiveNormalDefaultsForm";
import EDIT_USER from "../../graphql/mutations/EditUser";
import { UserPermissions } from "../../hooks/use-premium-feature";
import PremiumFeatureGate from "../common/premium/PremiumFeatureGate";
import { DSCardWrapper } from "../design-system/molecules";

function AutoSOAPSettingItem({
  onChange,
  valueToSet,
  title,
  desc,
  dataTestId,
}) {
  return (
    <DSCardWrapper
      variant={valueToSet ? "info" : "default"}
      interactive
      onClick={onChange}
      dataTestId={dataTestId}
      padding="p-md"
      className="flex-1 h-full"
    >
      <div className="flex flex-row items-center space-x-sm">
        <input
          onChange={onChange}
          checked={valueToSet}
          type={"radio"}
          value={valueToSet}
          className="accent-brand"
        />
        <label>
          <h5
            className={`font-semibold cursor-pointer ${
              valueToSet ? "text-brand" : "text-primary"
            }`}
          >
            {title}
          </h5>
        </label>
      </div>
      <p className="text-xs text-secondary">{desc}</p>
    </DSCardWrapper>
  );
}

// Define feature flag types
const FEATURE_FLAGS = {
  SONNET: "useSonnet",
  NAME_CORRECTION: "useNameCorrection",
  MULTI_LANG: "multiLanguageMode",
};

const SEGMENT_KEYS = {
  SONNET: "opt-out-sonnet",
  NAME_CORRECTION: "name-correction-toggle",
  MULTI_LANG: "multi-lang",
};

export function NoteSettings() {
  return (
    <div id="autoSOAPSection" className="flex-1 flex flex-col gap-md">
      <div className="w-full flex flex-col items-center space-y-md divide-y divide-secondary">
        <ObjectiveNormalDefaultsForm />
        <AccountItemLine dataTestId="name-correction-line">
          <PremiumFeatureGate
            featureName="Advanced Name Correction"
            iconPosition="middle-right"
            iconSize="sm"
            requiredPermission={
              UserPermissions.NAME_CORRECTION_ENABLED
            }
          >
            <FeatureToggle
              title="Advanced Name Correction"
              description="Notice your name being misspelled consistently? Enable this to automatically correct name misspellings in your future notes based on your account name."
              flagKey={FEATURE_FLAGS.NAME_CORRECTION}
              segmentKey={SEGMENT_KEYS.NAME_CORRECTION}
            />
          </PremiumFeatureGate>
        </AccountItemLine>
        <AccountItemLine dataTestId="multi-lang-line">
          <PremiumFeatureGate
            featureName="Multi Language Mode"
            iconPosition="middle-right"
            iconSize="sm"
            requiredPermission={UserPermissions.MULTILINGUAL_ENABLED}
          >
            <FeatureToggle
              title="Multi Language Mode"
              description="Conducting appointments in a language other than English? Toggle this on to convert conversations in Spanish, French, Chinese, Japanese, and more into English notes."
              flagKey={FEATURE_FLAGS.MULTI_LANG}
              segmentKey={SEGMENT_KEYS.MULTI_LANG}
            />
          </PremiumFeatureGate>
        </AccountItemLine>
      </div>
    </div>
  );
}

function AutoSoapToggle({
  shouldUseAutoSoaps,
  updateSetting,
  accountType,
}) {
  let planDerivedDescription =
    "Your recordings will be automatically converted to SOAPs. ";

  if (accountType !== "pro" || accountType !== "unlimited") {
    planDerivedDescription =
      "With this setting on, your first recording of the day will automatically be converted to a SOAP note. After that, your recordings will be saved as standard recordings. To unlock unlimited daily Auto-Notes, upgrade to a paid plan. ";
  }

  const desc =
    planDerivedDescription +
    "This will also enable a recording interface on the Dashboard of your Scribenote mobile app.";

  return (
    <AccountItemBlock
      title="Enable Auto-Notes:"
      desc="By default, all recordings you make will be Auto-Notes, meaning they will be turned into a note automatically. You can choose to disable this if you'd like your recordings to just be recordings."
    >
      <div className="flex flex-col md:flex-row md:items-center space-y-md md:space-y-0 md:space-x-md h-full">
        <AutoSOAPSettingItem
          onChange={() => updateSetting(true)}
          valueToSet={shouldUseAutoSoaps}
          title="Use Automation"
          desc={desc}
        />
        <AutoSOAPSettingItem
          onChange={() => updateSetting(false)}
          valueToSet={!shouldUseAutoSoaps}
          title="Don't Use Automation"
          desc="Your recordings will still be saved on Scribenote, but
        will not be automatically converted into notes."
          dataTestId="dont-use-automation"
        />
      </div>
    </AccountItemBlock>
  );
}

/**
 *
 * @param {*} shouldUseAutoSoaps Theusers account level setting
 * @returns
 */
export function AutoSoapSettingsAccount({ accountType, shouldUse }) {
  const [shouldUseOverride, setShouldUseOverride] = useState(null);
  const shouldUseAutoSoaps = shouldUseOverride ?? shouldUse;
  const { useLegacyNotes } = useFlags();

  const [editUser] = useMutation(EDIT_USER);

  function updateSetting(selection) {
    setShouldUseOverride(selection);
    editUser({
      variables: {
        shouldUseAutoSoaps: selection,
      },
    });
    mixpanel.track("Updated Account Setting", {
      settingName: "Enable Auto Notes",
      settingKey: "user.shouldUseAutoSoaps",
      settingValue: selection,
    });
  }

  if (!useLegacyNotes) {
    return null;
  }

  return (
    <AutoSoapToggle
      accountType={accountType}
      shouldUseAutoSoaps={shouldUseAutoSoaps}
      updateSetting={updateSetting}
    />
  );
}
